<template>
  <div class="min-h-screen bg-gray-50 pb-28 sm:pb-32">
    <!-- ══ HEADER & BUSCADOR PREMIUM RESPONSIVE (100% FONDO SÓLIDO) ══ -->
    <header class="bg-white border-b border-gray-200 sticky top-0 z-30 px-3 sm:px-6 py-2.5 sm:py-3.5 mb-4 sm:mb-6 shadow-sm">
      <div class="max-w-[1600px] mx-auto">
        <!-- Fila Principal de Navegación -->
        <div class="flex items-center justify-between gap-2 sm:gap-4 h-12 sm:h-14">
          <!-- IZQUIERDA: Marca / Volver -->
          <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button 
              class="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center bg-gray-50 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95"
              title="Ir al inicio"
              type="button"
              @click="$inertia.visit('/')"
            >
              <i class="fas fa-home text-sm sm:text-base" />
            </button>
            <div class="flex items-center">
              <logo class="fill-white hidden xs:block" width="100" height="42" />
              <logo class="fill-white xs:hidden" width="85" height="36" />
            </div>
          </div>

          <!-- CENTRO: Buscador Desktop (md+) -->
          <div v-if="mode === 'cart'" class="flex-1 max-w-2xl hidden md:flex items-center gap-3 transition-all">
            <div class="relative flex-1 group">
              <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-orange-500 transition-colors pointer-events-none" />
              <input 
                id="search"
                v-model="form.search.terms"
                type="text"
                placeholder="Buscar productos o escanear código..." 
                class="w-full pl-11 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-orange-500 text-sm font-medium transition-all placeholder-gray-400"
                @keydown.enter="onSearchProductsChange"
              />
              <button 
                v-if="form.search.terms" 
                type="button"
                class="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                @click="clearSearch"
              >
                <i class="fas fa-times text-[10px]" />
              </button>
            </div>
            <div class="h-8 w-px bg-gray-200" />
            <select 
              v-model="form.search.category" 
              class="w-40 py-2.5 px-3 bg-transparent border-none rounded-2xl focus:ring-0 text-xs font-black text-gray-500 uppercase tracking-wider cursor-pointer hover:text-orange-600 transition-colors"
            >
              <option value="">Categorías</option>
              <option v-for="category in product_categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>

          <!-- DERECHA: Tasa de Cambio & Acciones -->
          <div class="flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
            <!-- Botón Tablero de Comandas (Modo Restaurante) -->
            <Link 
              v-if="isRestaurantMode" 
              href="/boards" 
              class="h-9 px-3 sm:h-11 sm:px-4 rounded-xl sm:rounded-2xl bg-orange-50 hover:bg-orange-100 text-orange-600 font-bold text-xs uppercase tracking-wider transition-all active:scale-95 flex items-center gap-1.5 border border-orange-200"
              title="Ir al Tablero de Comandas"
            >
              <i class="fas fa-columns text-xs text-orange-500" />
              <span class="hidden md:inline">Comandas</span>
            </Link>
            
            <!-- Tasa Cambio Desktop -->
            <div class="hidden xl:flex flex-col items-end mr-2">
              <p class="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Tasa Cambio</p>
              <p class="text-xs font-black text-gray-900">1$ = <span class="text-orange-600">{{ change }} Bs.</span></p>
            </div>

            <!-- Tasa Cambio Móvil / Tablet -->
            <div class="xl:hidden flex items-center bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-lg">
              <span class="text-[10px] font-bold text-orange-700">1$ = {{ change }} Bs</span>
            </div>

            <!-- Botón Carrito en Móvil (Abre Bottom Sheet) -->
            <button 
              v-if="mode === 'cart'"
              type="button"
              class="relative xl:hidden h-9 px-3 rounded-xl bg-gray-900 text-white flex items-center gap-1.5 text-xs font-black active:scale-95 transition-all shadow-sm"
              aria-label="Ver carrito"
              @click="showMobileCart = true"
            >
              <i class="fas fa-shopping-basket text-xs sm:text-sm text-orange-400" />
              <span class="text-xs font-bold">${{ getTotal() }}</span>
              <span 
                v-if="cart.length" 
                class="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[9px] font-black flex items-center justify-center border-2 border-white"
              >
                {{ totalCartCount }}
              </span>
            </button>

            <!-- Botón de Retroceso en Modo Cliente -->
            <button 
              v-if="mode === 'client'"
              type="button"
              class="h-9 px-3 sm:h-11 sm:px-5 rounded-xl sm:rounded-2xl bg-gray-100 text-gray-600 hover:bg-gray-200 font-bold text-xs uppercase tracking-wider transition-all active:scale-95 flex items-center gap-1.5"
              @click="back"
            >
              <i class="fas fa-arrow-left text-[10px]" />
              <span class="hidden xs:inline">Volver</span>
            </button>

            <!-- Botón Finalizar Desktop (En móvil se opera desde la barra inferior para mayor ergonomía) -->
            <button 
              type="button"
              class="hidden xl:flex h-11 px-6 rounded-2xl bg-orange-500 text-white font-bold text-xs uppercase tracking-wider shadow-md hover:bg-orange-600 transition-all active:scale-95 items-center gap-2 group"
              @click="next"
            >
              <span v-if="mode === 'cart' && withoutClient">Finalizar</span>
              <span v-else-if="mode === 'cart'">Siguiente</span>
              <span v-else>Confirmar</span>
              <i class="fas fa-arrow-right text-[10px] group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>

        <!-- ══ BUSCADOR MÓVIL DEDICADO (< md) ══ -->
        <div v-if="mode === 'cart'" class="mt-2.5 md:hidden">
          <div class="relative flex items-center group">
            <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-orange-500 transition-colors pointer-events-none text-xs" />
            <input 
              id="search-mobile"
              v-model="form.search.terms"
              type="text"
              placeholder="Buscar productos o código de barra..." 
              class="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:bg-white text-xs font-medium transition-all placeholder-gray-400"
              @keydown.enter="onSearchProductsChange"
            />
            <button 
              v-if="form.search.terms" 
              type="button"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full bg-gray-200 transition-colors"
              @click="clearSearch"
            >
              <i class="fas fa-times text-[9px]" />
            </button>
          </div>
        </div>

        <!-- ══ CHIPS DE CATEGORÍAS DESLIZABLES ══ -->
        <div 
          v-if="mode === 'cart' && product_categories && product_categories.length" 
          class="mt-2.5 -mx-1 px-1 flex items-center gap-1.5 overflow-x-auto scrollbar-hide py-0.5"
        >
          <button 
            type="button"
            :class="form.search.category === '' ? 'bg-orange-500 text-white shadow-sm font-black' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold'"
            class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap transition-all flex-shrink-0"
            @click="setCategory('')"
          >
            Todos
          </button>
          <button 
            v-for="cat in product_categories" 
            :key="cat.id"
            type="button"
            :class="form.search.category == cat.id ? 'bg-orange-500 text-white shadow-sm font-black' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold'"
            class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap transition-all flex-shrink-0"
            @click="setCategory(cat.id)"
          >
            {{ cat.name }}
          </button>
        </div>
      </div>
    </header>

    <!-- ══ BARRA DE CUENTAS / MESAS (Múltiples Ventas Simultáneas para Restaurantes) ══ -->
    <div v-if="mode === 'cart' && isRestaurantMode" class="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 mb-4">
      <div class="bg-white rounded-2xl border border-gray-200/90 p-2 sm:p-2.5 shadow-sm flex items-center justify-between gap-2 overflow-hidden">
        <!-- Pestañas Deslizables de Cuentas -->
        <div class="flex items-center gap-2 overflow-x-auto scrollbar-hide py-0.5 flex-1 min-w-0">
          <div class="hidden sm:flex items-center gap-1.5 px-2.5 py-1 text-gray-400 font-bold text-xs uppercase tracking-wider flex-shrink-0">
            <i class="fas fa-layer-group text-orange-500" />
            <span>Cuentas:</span>
          </div>

          <!-- Botón de cada Cuenta / Mesa -->
          <div 
            v-for="tab in tabs" 
            :key="tab.id"
            :class="tab.id === activeTabId 
              ? 'bg-orange-50 border-orange-500 text-orange-950 shadow-sm ring-1 ring-orange-500/20' 
              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'"
            class="group cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs transition-all flex-shrink-0 select-none"
            @click="selectTab(tab.id)"
          >
            <i :class="getTabItemCount(tab) > 0 ? 'fas fa-receipt text-orange-600' : 'fas fa-utensils text-gray-400'" />

            <span class="font-bold max-w-[100px] sm:max-w-[130px] truncate">{{ tab.name }}</span>

            <span 
              v-if="getTabItemCount(tab) > 0"
              class="px-1.5 py-0.5 rounded-md bg-orange-500 text-white text-[10px] font-black"
            >
              {{ getTabItemCount(tab) }}
            </span>

            <span 
              v-if="Number(getTabTotal(tab)) > 0"
              class="font-black text-gray-900 text-xs pl-0.5"
            >
              ${{ getTabTotal(tab) }}
            </span>

            <button
              type="button"
              class="text-gray-400 hover:text-orange-600 ml-1 p-0.5 transition-colors opacity-70 group-hover:opacity-100"
              title="Cambiar nombre de cuenta / mesa"
              @click.stop="openRenameModal(tab)"
            >
              <i class="fas fa-pen text-[10px]" />
            </button>

            <button
              v-if="tabs.length > 1 || getTabItemCount(tab) > 0"
              type="button"
              class="text-gray-300 hover:text-rose-600 hover:bg-rose-50 rounded-full w-4 h-4 flex items-center justify-center transition-colors ml-0.5"
              title="Cerrar / Descartar cuenta"
              @click.stop="closeTab(tab.id)"
            >
              <i class="fas fa-times text-[10px]" />
            </button>
          </div>

          <!-- Botón Nueva Cuenta / Mesa -->
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs shadow-sm hover:shadow transition-all active:scale-95 flex-shrink-0"
            @click="addTab()"
          >
            <i class="fas fa-plus text-[10px]" />
            <span>Nueva Cuenta</span>
          </button>
        </div>

        <!-- Contador Total de Cuentas Abiertas -->
        <div class="hidden md:flex items-center gap-2 pl-3 border-l border-gray-200 flex-shrink-0">
          <span class="text-xs font-bold text-gray-500">
            <strong class="text-orange-600 font-black">{{ tabs.length }}</strong> {{ tabs.length === 1 ? 'cuenta abierta' : 'cuentas abiertas' }}
          </span>
        </div>
      </div>
    </div>

    <!-- ══ CUERPO DEL POS (Bento Grid) ══ -->
    <main class="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-8 items-start">
        <!-- SECCIÓN IZQUIERDA: PRODUCTOS (8/12 en Desktop) -->
        <div class="xl:col-span-8">
          <div v-if="mode === 'cart'">
            <!-- Contador de resultados para retroalimentación visual -->
            <div class="flex items-center justify-between mb-3 px-1 text-xs text-gray-400">
              <span class="font-medium">
                Mostrando <strong class="text-gray-700 font-black">{{ products.data.length }}</strong> productos
                <span v-if="form.search.terms || form.search.category">para tu búsqueda</span>
              </span>
              <button 
                v-if="form.search.terms || form.search.category" 
                type="button"
                class="text-orange-500 font-bold hover:underline text-[11px]"
                @click="resetAllFilters"
              >
                Limpiar filtros
              </button>
            </div>

            <!-- Grid de 2 columnas en Móvil / 3 columnas en Desktop -->
            <div 
              v-if="products.data.length"
              class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-6"
            >
              <product-card 
                v-for="product in products.data" 
                :key="product.id" 
                :product="product" 
                :cart-quantity="getCartItemQuantity(product.id)"
                :change="change" 
                :cop_change="cop_change" 
                @addCart="addCart"
              />
            </div>

            <!-- Estado Vacío -->
            <div v-else class="flex flex-col items-center justify-center py-16 sm:py-20 bg-white rounded-2xl sm:rounded-3xl border-2 border-dashed border-gray-200 p-6 text-center">
              <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-50 flex items-center justify-center mb-3 text-gray-300">
                <i class="fas fa-box-open text-2xl sm:text-3xl" />
              </div>
              <p class="text-gray-700 font-bold text-sm sm:text-base">No se encontraron productos</p>
              <p class="text-xs text-gray-400 mt-1 max-w-xs">Intenta con otro término de búsqueda o selecciona otra categoría</p>
              <button 
                type="button" 
                class="mt-4 px-4 py-2 bg-orange-50 text-orange-600 rounded-xl text-xs font-bold hover:bg-orange-100 transition-colors"
                @click="resetAllFilters"
              >
                Limpiar búsqueda y filtros
              </button>
            </div>
          </div>

          <!-- MODO: SELECCIÓN DE CLIENTE -->
          <div v-if="mode === 'client'" class="duration-300">
            <div class="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-200 overflow-hidden mb-8">
              <div class="p-4 sm:p-8 border-b border-gray-100 flex items-center gap-3 sm:gap-4">
                <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-inner">
                  <i class="fas fa-user-plus text-lg sm:text-xl" />
                </div>
                <div>
                  <h2 class="text-lg sm:text-2xl font-black text-gray-900 tracking-tight">Datos del Cliente</h2>
                  <p class="text-xs sm:text-sm text-gray-400 font-medium">Asocia esta venta a un cliente registrado o crea uno nuevo</p>
                </div>
              </div>

              <div class="p-4 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
                <div class="flex items-end gap-2 sm:gap-3">
                  <div class="flex-1">
                    <label class="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Identificación o Alias</label>
                    <input 
                      v-model="form.client.identification"
                      type="text" 
                      class="w-full px-4 sm:px-5 py-3 sm:py-3.5 bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                      placeholder="J-12345678-0"
                    />
                  </div>
                  <button 
                    class="h-[46px] sm:h-[52px] px-4 sm:px-6 bg-blue-600 text-white rounded-xl sm:rounded-2xl hover:bg-blue-700 transition-all active:scale-95 shadow-md shadow-blue-200 flex items-center justify-center gap-1.5"
                    type="button" 
                    @click="findClient"
                  >
                    <i class="fas fa-search text-xs" />
                    <span class="text-xs sm:text-sm font-bold uppercase tracking-wider">Buscar</span>
                  </button>
                </div>

                <text-input v-model="form.client.name" label="Nombre Completo" placeholder="Ej: Juan Pérez" class="rounded-xl sm:rounded-2xl bg-gray-50 border-none" />
                <text-input v-model="form.client.phone" label="Teléfono" placeholder="+58 414..." class="rounded-xl sm:rounded-2xl bg-gray-50 border-none" />
                <text-input v-model="form.client.address" label="Dirección" placeholder="San Diego, Valencia" class="rounded-xl sm:rounded-2xl bg-gray-50 border-none" />
                
                <div class="flex flex-col">
                  <label class="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Tipo de Cliente</label>
                  <select v-model="form.client.typeClient" class="w-full px-4 sm:px-5 py-3 sm:py-3.5 bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500 text-sm font-bold text-gray-700">
                    <option value="">Seleccionar tipo</option>
                    <option value="freelancer">Freelancer</option>
                    <option value="spot">SPOT</option>
                    <option value="a">A</option>
                    <option value="b">B</option>
                    <option value="c">C</option>
                  </select>
                </div>

                <div class="relative">
                  <text-input v-model="form.client.balance" disabled label="Saldo Pendiente" class="rounded-xl sm:rounded-2xl bg-gray-100 border-none text-rose-600 font-black" />
                  <i class="fas fa-exclamation-circle absolute right-4 bottom-3.5 text-rose-300" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ══ SECCIÓN DERECHA: CARRITO DESKTOP (4/12 en Desktop) ══ -->
        <div v-if="mode === 'cart'" class="hidden xl:block xl:col-span-4 sticky top-24">
          <div class="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden flex flex-col max-h-[calc(100vh-140px)]">
            <div class="p-5 border-b border-gray-100 flex items-center justify-between bg-white">
              <div class="flex items-center gap-2.5">
                <div class="w-10 h-10 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center">
                  <i class="fas fa-receipt" />
                </div>
                <div>
                  <div class="flex items-center gap-1.5">
                    <h2 class="text-base font-black text-gray-900 tracking-tight leading-tight">
                      {{ isRestaurantMode ? (currentTab ? currentTab.name : 'Tu Pedido') : 'Tu Pedido' }}
                    </h2>
                    <button 
                      v-if="isRestaurantMode"
                      type="button" 
                      class="text-gray-400 hover:text-orange-600 p-0.5 transition-colors" 
                      title="Cambiar nombre / mesa"
                      @click="openRenameModal(currentTab)"
                    >
                      <i class="fas fa-pen text-[10px]" />
                    </button>
                  </div>
                  <p class="text-[10px] text-gray-400 font-medium">{{ isRestaurantMode ? 'Cuenta activa' : 'Resumen de compra' }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-wider">
                  {{ totalCartCount }} {{ totalCartCount === 1 ? 'Artículo' : 'Artículos' }}
                </span>
                <button
                  v-if="isRestaurantMode"
                  type="button"
                  class="w-7 h-7 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 flex items-center justify-center text-xs transition-colors"
                  title="Abrir otra cuenta / mesa"
                  @click="addTab()"
                >
                  <i class="fas fa-plus" />
                </button>
              </div>
            </div>

            <!-- Lista de Productos en Carrito Desktop -->
            <div class="flex-1 overflow-y-auto p-6 space-y-3.5 scrollbar-hide">
              <div 
                v-for="product in cart" 
                :key="product.id" 
                class="group flex items-center gap-3.5 p-3.5 rounded-2xl bg-gray-50 border border-gray-100 hover:border-orange-200 hover:bg-orange-50 transition-all"
              >
                <!-- Foto Miniatura con dimensiones estrictas -->
                <div class="w-12 h-12 min-w-[3rem] min-h-[3rem] max-w-[3rem] max-h-[3rem] rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden p-1 shadow-sm">
                  <img 
                    v-if="product.photo && !cartImageErrors[product.id]" 
                    :src="product.photo" 
                    class="w-full h-full object-cover rounded-lg" 
                    alt="" 
                    @error="cartImageErrors[product.id] = true"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50 rounded-lg">
                    <i class="fas fa-utensils text-xs text-orange-200" />
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-xs font-bold text-gray-900 truncate leading-none mb-1">{{ product.name }}</h3>
                  <p class="text-[10px] text-gray-400 font-medium">Ref: {{ product.code }}</p>
                  
                  <!-- Controles de Cantidad con Stepper -->
                  <div class="flex items-center mt-2 gap-2">
                    <span class="text-xs font-black text-gray-900">${{ product.price }}</span>
                    <span class="text-[10px] text-gray-300">×</span>
                    
                    <div class="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
                      <button 
                        type="button" 
                        class="w-6 h-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-900 active:scale-90 transition-all"
                        @click="decrementQuantity(product)"
                      >
                        <i class="fas fa-minus text-[8px]" />
                      </button>
                      <input 
                        v-model.number="product.quantity"
                        type="number" 
                        min="1"
                        :max="product.stock"
                        class="w-8 h-6 border-none text-center text-xs font-black text-gray-900 p-0 focus:ring-0"
                      />
                      <button 
                        type="button" 
                        class="w-6 h-6 rounded flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-900 active:scale-90 transition-all"
                        @click="incrementQuantity(product)"
                      >
                        <i class="fas fa-plus text-[8px]" />
                      </button>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:bg-rose-50 hover:text-rose-500 transition-all"
                  title="Eliminar del pedido"
                  @click="removeProduct(product)"
                >
                  <i class="fas fa-times-circle" />
                </button>
              </div>

              <!-- Carrito Vacío Desktop -->
              <div v-if="!cart.length" class="flex flex-col items-center justify-center py-12 text-center">
                <div class="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-gray-200 mb-4">
                  <i class="fas fa-cart-plus text-2xl" />
                </div>
                <p class="text-sm text-gray-400 font-bold">Carrito vacío</p>
                <p class="text-[10px] text-gray-300 mt-1 max-w-[150px]">Agrega productos haciendo clic en el botón de la lista</p>
              </div>
            </div>

            <!-- TOTALES CARRITO DESKTOP -->
            <div class="p-6 bg-orange-50 border-t border-orange-100">
              <div class="space-y-2 mb-4">
                <div class="flex justify-between items-center text-orange-700">
                  <span class="text-xs font-bold uppercase tracking-wider">Total Bs.</span>
                  <span class="text-sm font-mono font-bold">Bs. {{ (getTotal() * change).toLocaleString(undefined, {minimumFractionDigits: 2}) }}</span>
                </div>
              </div>
              
              <div class="flex justify-between items-end">
                <div>
                  <p class="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-0.5">Monto a Pagar</p>
                  <h2 class="text-3xl font-black tracking-tight text-gray-900">
                    <span class="text-lg font-medium text-orange-400 mr-0.5">$</span>{{ getTotal() }}
                  </h2>
                </div>
                <button 
                  v-if="cart.length" 
                  type="button" 
                  class="text-[11px] font-bold text-rose-500 hover:underline mb-1"
                  @click="clearCart"
                >
                  Vaciar carrito
                </button>
              </div>

              <div class="mt-4 pt-4 border-t border-orange-200">
                <label class="flex items-center gap-3 cursor-pointer group select-none">
                  <div class="relative">
                    <input v-model="withoutClient" type="checkbox" class="sr-only" />
                    <div :class="withoutClient ? 'bg-orange-500' : 'bg-gray-300'" class="w-10 h-5 rounded-full transition-colors" />
                    <div :class="withoutClient ? 'translate-x-5' : 'translate-x-1'" class="absolute top-1 left-0 w-3 h-3 bg-white rounded-full transition-transform shadow" />
                  </div>
                  <span class="text-xs font-bold text-orange-950 group-hover:text-orange-600 transition-colors">Venta rápida (Sin cliente)</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- ══ BARRA INFERIOR FIJA PARA MÓVILES (xl:hidden, 100% FONDO SÓLIDO) ══ -->
    <div 
      v-if="mode === 'cart'"
      class="xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl px-3 sm:px-4 py-2.5 sm:py-3"
    >
      <div class="flex items-center justify-between gap-3 max-w-lg mx-auto">
        <!-- Tappable para abrir Drawer del Carrito -->
        <button 
          type="button" 
          class="flex items-center gap-2.5 sm:gap-3 text-left focus:outline-none group flex-1 min-w-0"
          @click="showMobileCart = true"
        >
          <div class="relative w-10 h-10 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0 shadow group-active:scale-95 transition-transform">
            <i class="fas fa-shopping-basket text-sm sm:text-base text-orange-400" />
            <span 
              v-if="cart.length" 
              class="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[9px] font-black flex items-center justify-center border-2 border-white"
            >
              {{ totalCartCount }}
            </span>
          </div>
          <div class="min-w-0">
            <div class="flex items-baseline gap-1.5">
              <span class="text-base sm:text-lg font-black text-gray-900 leading-none">${{ getTotal() }}</span>
              <span class="text-[10px] font-bold text-orange-600 leading-none truncate">Bs. {{ (getTotal() * change).toLocaleString(undefined, {minimumFractionDigits: 2}) }}</span>
            </div>
            <p class="text-[10px] font-bold text-gray-400 mt-1 flex items-center gap-1 group-hover:text-orange-500 transition-colors">
              <span>{{ cart.length ? 'Ver pedido (' + totalCartCount + ')' : 'Carrito vacío' }}</span>
              <i class="fas fa-chevron-up text-[8px]" />
            </p>
          </div>
        </button>

        <!-- Botón Directo Cobrar / Siguiente -->
        <button 
          :disabled="!cart.length"
          type="button"
          class="h-11 px-5 sm:px-6 rounded-xl sm:rounded-2xl bg-orange-500 text-white font-bold text-xs uppercase tracking-wider shadow-md hover:bg-orange-600 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
          @click="next"
        >
          <span v-if="withoutClient">Cobrar</span>
          <span v-else>Siguiente</span>
          <i class="fas fa-arrow-right text-[10px]" />
        </button>
      </div>
    </div>

    <!-- ══ BOTTOM SHEET (DRAWER) DEL CARRITO PARA MÓVILES (xl:hidden) ══ -->
    <div v-if="showMobileCart" class="xl:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <!-- Backdrop con cierre al tocar fondo -->
      <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="showMobileCart = false" />

      <!-- Contenedor del Sheet (100% fondo blanco sólido) -->
      <div class="relative bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col z-50">
        <!-- Manija de arrastre visual -->
        <div class="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-1" />

        <!-- Cabecera del Drawer -->
        <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-white">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center text-sm">
              <i class="fas fa-receipt" />
            </div>
            <div>
              <div class="flex items-center gap-1.5">
                <h2 class="text-sm sm:text-base font-black text-gray-900 leading-tight">
                  {{ isRestaurantMode ? (currentTab ? currentTab.name : 'Tu Pedido') : 'Tu Pedido' }}
                </h2>
                <button 
                  v-if="isRestaurantMode"
                  type="button" 
                  class="text-gray-400 hover:text-orange-600 text-xs p-0.5" 
                  @click="openRenameModal(currentTab)"
                >
                  <i class="fas fa-pen text-[10px]" />
                </button>
              </div>
              <p class="text-[10px] text-gray-400 font-medium">
                {{ totalCartCount }} {{ totalCartCount === 1 ? 'artículo seleccionado' : 'artículos seleccionados' }}
              </p>
            </div>
          </div>
          <button 
            type="button" 
            class="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center text-xs transition-colors"
            @click="showMobileCart = false"
          >
            <i class="fas fa-times" />
          </button>
        </div>

        <!-- Lista de Productos en el Drawer -->
        <div class="flex-1 overflow-y-auto p-4 space-y-2.5 bg-white">
          <div 
            v-for="product in cart" 
            :key="product.id"
            class="flex items-center gap-3 p-3 rounded-2xl bg-gray-50 border border-gray-100"
          >
            <!-- Foto Miniatura -->
            <div class="w-12 h-12 min-w-[3rem] min-h-[3rem] max-w-[3rem] max-h-[3rem] rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden p-1 shadow-sm">
              <img 
                v-if="product.photo && !cartImageErrors[product.id]" 
                :src="product.photo" 
                class="w-full h-full object-cover rounded-lg" 
                alt="" 
                @error="cartImageErrors[product.id] = true"
              />
              <div v-else class="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50 rounded-lg">
                <i class="fas fa-utensils text-xs text-orange-200" />
              </div>
            </div>
            
            <!-- Detalles -->
            <div class="flex-1 min-w-0">
              <h3 class="text-xs font-bold text-gray-900 truncate leading-snug">{{ product.name }}</h3>
              <div class="flex items-center gap-2 mt-0.5">
                <span class="text-xs font-black text-gray-900">${{ product.price }}</span>
                <span class="text-[10px] text-orange-600 font-semibold">Bs. {{ (product.price * change).toFixed(2) }}</span>
              </div>
            </div>

            <!-- Stepper de Cantidades Ergonómico -->
            <div class="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm flex-shrink-0">
              <button 
                type="button" 
                class="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-xs font-bold active:scale-90 transition-all"
                @click="decrementQuantity(product)"
              >
                <i class="fas fa-minus text-[8px]" />
              </button>
              <span class="w-6 text-center text-xs font-black text-gray-900">{{ product.quantity }}</span>
              <button 
                type="button" 
                class="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-xs font-bold active:scale-90 transition-all"
                @click="incrementQuantity(product)"
              >
                <i class="fas fa-plus text-[8px]" />
              </button>
            </div>

            <!-- Botón Eliminar -->
            <button 
              type="button" 
              class="w-7 h-7 rounded-lg text-gray-300 hover:text-rose-500 hover:bg-rose-50 flex items-center justify-center transition-colors flex-shrink-0"
              @click="removeProduct(product)"
            >
              <i class="fas fa-trash-alt text-xs" />
            </button>
          </div>

          <!-- Drawer Vacío -->
          <div v-if="!cart.length" class="py-12 text-center">
            <div class="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mx-auto mb-3 text-xl">
              <i class="fas fa-cart-plus" />
            </div>
            <p class="text-sm font-bold text-gray-400">Carrito vacío</p>
            <p class="text-xs text-gray-300 mt-1">Agrega productos tocando el botón "+ Agregar"</p>
          </div>
        </div>

        <!-- Footer del Drawer: Switch de Venta Rápida, Totales y Botón Principal -->
        <div class="p-4 bg-gray-50 border-t border-gray-200">
          <!-- Switch Venta Rápida -->
          <div class="mb-3">
            <label class="flex items-center justify-between cursor-pointer p-2 rounded-xl bg-white border border-gray-200 select-none">
              <div>
                <span class="text-xs font-bold text-gray-800 block">Venta rápida (Sin cliente)</span>
                <span class="text-[10px] text-gray-400 block">Crea la venta inmediatamente sin formulario</span>
              </div>
              <div class="relative">
                <input v-model="withoutClient" type="checkbox" class="sr-only" />
                <div :class="withoutClient ? 'bg-orange-500' : 'bg-gray-300'" class="w-10 h-5 rounded-full transition-colors" />
                <div :class="withoutClient ? 'translate-x-5' : 'translate-x-1'" class="absolute top-1 left-0 w-3 h-3 bg-white rounded-full transition-transform shadow" />
              </div>
            </label>
          </div>

          <!-- Desglose de Totales -->
          <div class="flex items-center justify-between mb-3 px-1">
            <div>
              <span class="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Total a Cobrar</span>
              <div class="flex items-baseline gap-2">
                <span class="text-2xl font-black text-gray-900">${{ getTotal() }}</span>
                <span class="text-xs font-bold text-orange-600">Bs. {{ (getTotal() * change).toLocaleString(undefined, {minimumFractionDigits: 2}) }}</span>
              </div>
            </div>
            <button 
              v-if="cart.length" 
              type="button" 
              class="text-[11px] font-bold text-rose-500 hover:underline"
              @click="clearCart"
            >
              Vaciar carrito
            </button>
          </div>

          <!-- Botón de Confirmación -->
          <button 
            :disabled="!cart.length"
            type="button"
            class="w-full py-3.5 rounded-2xl bg-orange-500 text-white font-bold text-xs uppercase tracking-widest shadow-lg hover:bg-orange-600 transition-all active:scale-95 disabled:opacity-40 flex items-center justify-center gap-2"
            @click="proceedFromSheet"
          >
            <span v-if="withoutClient">Finalizar Venta (${{ getTotal() }})</span>
            <span v-else>Continuar a Datos de Cliente</span>
            <i class="fas fa-arrow-right text-[10px]" />
          </button>
        </div>
      </div>
    </div>

    <!-- ══ BARRA DE ACCIÓN FLOTANTE (Solo cuando aplica estimado de vuelto o botón atrás) ══ -->
    <div v-if="cambio > 0 || mode !== 'cart'" class="fixed bottom-20 sm:bottom-8 left-4 sm:left-8 z-40 w-auto max-w-3xl">
      <div class="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-xl flex items-center gap-4 overflow-hidden">
        <div v-if="cambio > 0" class="flex flex-col items-start pl-2">
          <p class="text-[9px] font-black text-orange-500 uppercase tracking-tighter">Vuelto Estimado</p>
          <p class="text-base sm:text-lg font-black text-gray-900 leading-none mt-0.5">${{ (cambio - getTotal()).toFixed(2) }}</p>
        </div>

        <div v-if="mode !== 'cart'" class="flex items-center gap-2">
          <button 
            type="button"
            class="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-600 font-bold text-xs uppercase tracking-wider hover:bg-gray-200 transition-all active:scale-95 flex items-center gap-1.5"
            @click="back"
          >
            <i class="fas fa-arrow-left text-[10px]" />
            <span>Volver al Carrito</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL PARA IDENTIFICAR / RENOMBRAR CUENTA / MESA ══ -->
    <div v-if="showRenameModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-black bg-opacity-40 transition-opacity" @click="closeRenameModal" />
      
      <div class="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-gray-100 z-50">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2.5 text-gray-900">
            <div class="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-sm">
              <i class="fas fa-tag" />
            </div>
            <h3 class="font-bold text-base">Identificar Cuenta / Mesa</h3>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="closeRenameModal">
            <i class="fas fa-times text-sm" />
          </button>
        </div>

        <form @submit.prevent="saveTabName">
          <div class="mb-4">
            <label class="block text-xs font-bold text-gray-600 mb-1">Nombre o referencia de la cuenta</label>
            <input
              ref="renameInput"
              v-model="editTabNameInput"
              type="text"
              placeholder="Ej: Mesa 4, Barra, Pedido #12, Carlos"
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:bg-white text-sm font-semibold text-gray-900"
              maxlength="30"
              required
            />
          </div>

          <!-- Sugerencias rápidas para restaurantes -->
          <div class="mb-5">
            <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Sugerencias rápidas:</p>
            <div class="flex flex-wrap gap-1.5">
              <button 
                v-for="preset in ['Mesa 1', 'Mesa 2', 'Mesa 3', 'Mesa 4', 'Mesa 5', 'Barra', 'Terraza', 'Para llevar']"
                :key="preset"
                type="button"
                class="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-orange-100 hover:text-orange-700 text-gray-700 transition-colors"
                @click="editTabNameInput = preset"
              >
                {{ preset }}
              </button>
            </div>
          </div>

          <div class="flex items-center justify-end gap-2">
            <button 
              type="button" 
              class="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 transition-colors" 
              @click="closeRenameModal"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              class="px-5 py-2 rounded-xl text-xs font-bold bg-orange-600 hover:bg-orange-700 text-white shadow-sm transition-all"
            >
              Guardar Nombre
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutFull from '../../Shared/LayoutFull.vue'
import ProductCard from '../../Shared/ProductCard.vue'
import TextInput from '../../Shared/TextInput.vue'
import TextInputRounded from '../../Shared/TextInputRounded.vue'
import SelectInput from '../../Shared/SelectInput.vue'
import Logo from '../../Shared/Logo.vue'
import { Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'

export default {
  components: { 
    ProductCard, 
    TextInput, 
    TextInputRounded, 
    SelectInput,
    Logo,
    Link,
  },

  layout: LayoutFull,

  props: {
    products: Object,
    change: Number,
    cop_change: Number,
    client: Object,
    product_categories: Object,
    restaurant_mode: Boolean,
  },

  data() {
    return {
      cambio: 0,
      showClientCreate: false,
      mode: 'cart',
      showMobileCart: false,
      cartImageErrors: {},
      tabs: [
        {
          id: 'tab-1',
          name: 'Mesa 1',
          cart: [],
          withoutClient: true,
          note: '',
        },
      ],
      activeTabId: 'tab-1',
      showRenameModal: false,
      renamingTabId: null,
      editTabNameInput: '',
      form: this.$inertia.form({
        search: {
          terms: '',
          category: '',
        },
        client: {
          identification: '',
        },
        products: null,
        isCredit: false,
        comentary: 'Compra rápida!',
        referencePay: '',
        quick: true,
        order_state: null,
      }),
    }
  },

  computed: {
    isRestaurantMode() {
      if (typeof this.restaurant_mode === 'boolean') {
        return this.restaurant_mode
      }
      return !!this.$page?.props?.auth?.user?.account?.restaurant_mode
    },

    currentTab() {
      const found = this.tabs.find(t => t.id === this.activeTabId)
      return found || this.tabs[0] || null
    },

    cart: {
      get() {
        return this.currentTab ? this.currentTab.cart : []
      },
      set(val) {
        if (this.currentTab) {
          this.currentTab.cart = val
          this.saveTabsToStorage()
        }
      },
    },

    withoutClient: {
      get() {
        return this.currentTab ? this.currentTab.withoutClient : true
      },
      set(val) {
        if (this.currentTab) {
          this.currentTab.withoutClient = val
          this.saveTabsToStorage()
        }
      },
    },

    totalCartCount() {
      return this.cart.reduce((total, p) => total + Number(p.quantity || 1), 0)
    },
  },

  watch: {
    tabs: {
      deep: true,
      handler() {
        this.saveTabsToStorage()
      },
    },

    'form.search.terms': function() {
      this.debouncedSearch()
    },

    'form.search.category': function() {
      this.onSearchProductsChange()
    },

    'products': function(val) {
      if(this.products?.data?.length === 1) {
        const product = this.products.data[0]
        
        if(product.code === this.form.search.terms) {
          if (product.type_product !== 'service' && product.quantity <= 0) {
            this.form.search.terms = ''
            return alert('No hay stock disponible de este producto')
          }

          const existingProduct = this.cart.find(p => p.id === product.id)
          
          if (existingProduct) {
            if (product.type_product !== 'service' && existingProduct.quantity >= product.quantity) {
              this.form.search.terms = ''
              return alert('No hay más stock disponible de este producto')
            }
            existingProduct.quantity++
          } else {
            this.cart.push({
              ...product,
              stock: product.quantity,
              quantity: 1,
              price: product?.prices?.[0]?.price ?? product.price,
              quick: false,
            })
          }

          this.form.search.terms = ''
        }
      }
    },
  },

  methods: {
    getTabTotal(tab) {
      if (!tab || !tab.cart) return '0.00'
      return tab.cart.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2)
    },

    getTabItemCount(tab) {
      if (!tab || !tab.cart) return 0
      return tab.cart.reduce((sum, p) => sum + Number(p.quantity || 1), 0)
    },

    addTab(customName) {
      const nextNum = this.tabs.length + 1
      const newTab = {
        id: 'tab-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
        name: customName || `Mesa ${nextNum}`,
        cart: [],
        withoutClient: true,
        note: '',
      }
      this.tabs.push(newTab)
      this.activeTabId = newTab.id
      this.saveTabsToStorage()
    },

    selectTab(tabId) {
      this.activeTabId = tabId
      this.saveTabsToStorage()
    },

    closeTab(tabId) {
      const target = this.tabs.find(t => t.id === tabId)
      if (!target) return

      if (target.cart && target.cart.length > 0) {
        if (!confirm(`La cuenta "${target.name}" tiene ${target.cart.length} productos agregados. ¿Deseas cerrarla y descartar los productos?`)) {
          return
        }
      }

      this.tabs = this.tabs.filter(t => t.id !== tabId)
      if (this.tabs.length === 0) {
        this.addTab('Mesa 1')
      } else if (this.activeTabId === tabId) {
        this.activeTabId = this.tabs[0].id
      }
      this.saveTabsToStorage()
    },

    openRenameModal(tab) {
      const target = tab || this.currentTab
      if (!target) return
      this.renamingTabId = target.id
      this.editTabNameInput = target.name
      this.showRenameModal = true
      this.$nextTick(() => {
        if (this.$refs.renameInput) {
          this.$refs.renameInput.focus()
          this.$refs.renameInput.select()
        }
      })
    },

    closeRenameModal() {
      this.showRenameModal = false
      this.renamingTabId = null
      this.editTabNameInput = ''
    },

    saveTabName() {
      if (!this.editTabNameInput || !this.editTabNameInput.trim()) return
      const target = this.tabs.find(t => t.id === this.renamingTabId)
      if (target) {
        target.name = this.editTabNameInput.trim()
        this.saveTabsToStorage()
      }
      this.closeRenameModal()
    },

    loadTabsFromStorage() {
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          const saved = localStorage.getItem('rg_pos_tabs')
          if (saved) {
            const parsed = JSON.parse(saved)
            if (Array.isArray(parsed) && parsed.length > 0) {
              this.tabs = parsed
              const savedActive = localStorage.getItem('rg_pos_active_tab')
              if (savedActive && this.tabs.some(t => t.id === savedActive)) {
                this.activeTabId = savedActive
              } else {
                this.activeTabId = this.tabs[0].id
              }
              return
            }
          }
        } catch (e) {
          console.error('Error cargando cuentas del POS', e)
        }
      }

      if (!this.tabs || this.tabs.length === 0) {
        this.tabs = [{
          id: 'tab-' + Date.now(),
          name: this.isRestaurantMode ? 'Mesa 1' : 'Pedido Principal',
          cart: [],
          withoutClient: true,
          note: '',
        }]
      }
      this.activeTabId = this.tabs[0].id
    },

    saveTabsToStorage() {
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem('rg_pos_tabs', JSON.stringify(this.tabs))
          if (this.activeTabId) {
            localStorage.setItem('rg_pos_active_tab', this.activeTabId)
          }
        } catch (e) {
          console.error('Error guardando cuentas del POS', e)
        }
      }
    },

    removePaidTab(tabId) {
      this.tabs = this.tabs.filter(t => t.id !== tabId)
      if (this.tabs.length === 0) {
        this.addTab(this.isRestaurantMode ? 'Mesa 1' : 'Pedido Principal')
      } else {
        this.activeTabId = this.tabs[0].id
      }
      this.saveTabsToStorage()
    },

    debouncedSearch: debounce(function() {
      this.onSearchProductsChange()
    }, 250),

    clearSearch() {
      this.form.search.terms = ''
      this.onSearchProductsChange()
    },

    setCategory(catId) {
      this.form.search.category = catId
      this.onSearchProductsChange()
    },

    resetAllFilters() {
      this.form.search.terms = ''
      this.form.search.category = ''
      this.onSearchProductsChange()
    },

    getCartItemQuantity(productId) {
      const item = this.cart.find(p => p.id === productId)
      return item ? Number(item.quantity) : 0
    },

    getTotal() {
      return this.cart.reduce((total, product) => {
        return total + (product.price * product.quantity)
      }, 0).toFixed(2)
    },

    incrementQuantity(product) {
      if (product.type_product !== 'service' && product.stock && product.quantity >= product.stock) {
        return alert('No hay más stock disponible de este producto')
      }
      product.quantity++
    },

    decrementQuantity(product) {
      if (product.quantity > 1) {
        product.quantity--
      } else {
        this.removeProduct(product)
      }
    },

    clearCart() {
      if (confirm('¿Desea vaciar todos los productos del carrito de esta cuenta?')) {
        this.cart = []
        this.showMobileCart = false
      }
    },

    proceedFromSheet() {
      this.showMobileCart = false
      this.next()
    },

    back() {
      if(this.mode === 'client') {
        this.mode = 'cart'
      }
    },

    next() {
      if(this.mode === 'cart') {
        if(this.cart.length === 0) {
          return alert('Debe agregar productos al carrito para continuar')
        }

        if(this.withoutClient) {
          this.form.client = {
            name: 'Cliente por defecto',
            email: null,
            phone: '0414409888391',
            address: 'San diego',
            identification: '0000',
            balance: 0,
            typeClient: 'a',
            companyName: null,
            id: 1,
          }
          this.form.products = this.cart
          const tabPrefix = (this.isRestaurantMode && this.currentTab?.name) ? `[${this.currentTab.name}] ` : ''
          this.form.comentary = tabPrefix + (this.currentTab?.note || 'Compra rápida!')
          if (this.isRestaurantMode) {
            this.form.order_state = 'por_iniciar'
          }

          const paidTabId = this.currentTab?.id
          return this.form.post('/orders', {
            onSuccess: () => {
              this.removePaidTab(paidTabId)
            },
          })
        }
        this.mode = 'client'
      } else if(this.mode === 'client') {
        this.form.products = this.cart
        const tabPrefix = (this.isRestaurantMode && this.currentTab?.name) ? `[${this.currentTab.name}] ` : ''
        this.form.comentary = tabPrefix + (this.currentTab?.note || 'Compra rápida!')
        if (this.isRestaurantMode) {
          this.form.order_state = 'por_iniciar'
        }

        const paidTabId = this.currentTab?.id
        this.form.post('/orders', {
          onSuccess: () => {
            this.removePaidTab(paidTabId)
          },
        })
      } else if(this.mode === 'pay') {
        this.mode = 'print'
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.cart = this.cart.filter(p => p.id !== product.id)
      }
    },

    addCart(product) {
      if (product.type_product !== 'service' && product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const existingProduct = this.cart.find(p => p.id === product.id)
      
      if (existingProduct) {
        if (product.type_product !== 'service' && existingProduct.quantity >= product.quantity) {
          return alert('No hay más stock disponible de este producto')
        }
        existingProduct.quantity++
      } else {
        this.cart.push({
          ...product,
          stock: product.quantity,
          quantity: 1,
          price: product?.prices?.[0]?.price ?? product.price,
          quick: false,
          fee: 0,
        })
      }
    },

    onSearchProductsChange() {
      this.$inertia.get('/pos', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },

    findClient() {
      this.$inertia.get('/pos', pickBy(this.form), { preserveState: true })

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient,
            companyName: this.client.companyName,
          }
        } else {
          alert('No se encontró el cliente. Puede registrarlo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },
  },

  mounted() {
    this.loadTabsFromStorage()
    // En pantallas de escritorio enfocar búsqueda automáticamente.
    // En móviles no se auto-enfoca para evitar que el teclado virtual tape los productos al cargar.
    if (window.innerWidth >= 768) {
      const searchInput = document.getElementById('search')
      if (searchInput) searchInput.focus()
    }
  },
}
</script>
